export interface TgUser {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
}

export interface TgWebApp {
  initData: string;
  initDataUnsafe: {
    user?: TgUser;
    auth_date?: number;
    hash?: string;
    query_id?: string;
  };
  version: string;
  themeParams: Record<string, string>;
  colorScheme: 'light' | 'dark';
  expand: () => void;
  ready: () => void;
  close: () => void;
  showAlert: (m: string) => void;
  HapticFeedback?: {
    impactOccurred: (s: 'light' | 'medium' | 'heavy') => void;
    notificationOccurred: (s: 'error' | 'success' | 'warning') => void;
  };
  MainButton?: {
    setText: (t: string) => void;
    show: () => void;
    hide: () => void;
    onClick: (f: () => void) => void;
  };
}

const TELEGRAM_HOST = 'tg.aniphys.ru';
const SDK_TIMEOUT_MS = 8_000;
let sdkPromise: Promise<void> | null = null;

export function isTelegramHost(): boolean {
  return window.location.hostname === TELEGRAM_HOST;
}

export function loadTelegramSdk(): Promise<void> {
  if (!isTelegramHost() || getTgWebApp()) return Promise.resolve();
  if (sdkPromise) return sdkPromise;

  const request = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    const timer = window.setTimeout(() => {
      script.remove();
      reject(new Error('Telegram SDK timed out'));
    }, SDK_TIMEOUT_MS);
    const settle = (callback: () => void) => {
      window.clearTimeout(timer);
      callback();
    };
    script.src = 'https://telegram.org/js/telegram-web-app.js?63';
    script.onload = () => settle(() => resolve());
    script.onerror = () => settle(() => reject(new Error('Telegram SDK failed to load')));
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    sdkPromise = null;
    throw error;
  });

  sdkPromise = request;
  return request;
}

export function getTgWebApp(): TgWebApp | null {
  const w = window as unknown as { Telegram?: { WebApp?: TgWebApp } };
  return w.Telegram?.WebApp ?? null;
}

export function isInTelegram(): boolean {
  if (!isTelegramHost()) return false;
  const tg = getTgWebApp();
  return !!tg && !!tg.initData && tg.initData.length > 0;
}

export const TG_WIDGET_BOT = String(import.meta.env.VITE_TG_BOT_USERNAME ?? '').replace(/^@/, '');

export function telegramLoginAvailable(): boolean {
  return TG_WIDGET_BOT !== '' && !isInTelegram();
}
